import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Lightbulb, Thermometer, Blinds, DoorOpen, Droplets, Zap, Shield, Smartphone, TrendingDown, Clock } from "lucide-react";


export default function SmartHomeAutomation() {

  // useEffect(() => {
  //   if (!sessionStorage.getItem("smartHomeReloaded")) {
  //     sessionStorage.setItem("smartHomeReloaded", "true");
  //     if (performance.getEntriesByType("navigation")[0].type !== "reload") {
  //       window.location.reload();
  //     }
  //   } else {
  //     sessionStorage.removeItem("smartHomeReloaded");
  //   }
  // }, []);

  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";

  const features = [
    { icon: Lightbulb, key: "lighting", color: "#ffde21" },
    { icon: Thermometer, key: "ac", color: "#ffde21" },
    { icon: Blinds, key: "shutters", color: "#ffde21" },
    { icon: DoorOpen, key: "motors", color: "#ffde21" },
    { icon: Droplets, key: "tanks", color: "#ffde21" },
  ];

  const benefitIcons = [Zap, Shield, Smartphone, TrendingDown, Clock];
  const benefits = t("smart_home_benefits", { returnObjects: true });
  const benefitsArray = Object.values(benefits);

  return (
    <div dir={dir} className="min-h-screen" style={{ background: '#fafcf0' }}>
      {/* Hero Section - Modern & Bold */}
      <section className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #2d2d2b 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="flex flex-col md:flex-row items-center gap-12 md:gap-16">
            {/* Image Side */}
            <motion.div
              initial={{ opacity: 0, x: dir === "rtl" ? 50 : -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="md:w-1/2"
            >
              <div className="relative">
                <div
                  className="absolute -inset-4 rounded-3xl opacity-20 blur-2xl"
                  style={{ background: '#ffde21' }}
                ></div>
                <img
                  src="/images/smart-home.webp"
                  alt={t("smart_home_title")}
                  className="relative rounded-3xl shadow-2xl w-full object-cover"
                  style={{ border: '3px solid #ffde21' }}
                  fetchpriority="high"
                  decoding="async"
                />
              </div>
            </motion.div>

            {/* Text Side */}
            <motion.div
              initial={{ opacity: 0, x: dir === "rtl" ? 50 : -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="md:w-1/2"
            >
              <div className="inline-block mb-4">
                <span
                  className="px-4 py-2 rounded-full text-sm font-bold"
                  style={{
                    background: 'rgba(255, 222, 33, 0.2)',
                    color: '#2d2d2b'
                  }}
                >
                  {dir === "rtl" ? "أحدث التقنيات" : "Latest Technology"}
                </span>
              </div>

              <h1
                className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight"
                style={{ color: '#2d2d2b' }}
              >
                {t("smart_home_title")}
              </h1>

              <p
                className="text-lg md:text-xl leading-relaxed mb-8"
                style={{ color: '#2d2d2b', opacity: 0.8 }}
              >
                {t("smart_home_desc")}
              </p>

              <Link to="/design-home">
                <button
                  className="px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                  style={{
                    background: '#ffde21',
                    color: '#2d2d2b'
                  }}
                >
                  {dir === "rtl" ? "اطلب استشارة مجانية" : "Get Free Consultation"}
                </button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section - Modern Cards */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-black mb-4"
              style={{ color: '#2d2d2b' }}
            >
              {dir === "rtl" ? "ميزات التحكم الذكي" : "Smart Control Features"}
            </h2>
            <p
              className="text-lg max-w-2xl mx-auto"
              style={{ color: '#2d2d2b', opacity: 0.7 }}
            >
              {dir === "rtl"
                ? "تحكم كامل في منزلك من أي مكان وفي أي وقت"
                : "Complete control of your home from anywhere, anytime"}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="relative p-8 rounded-2xl transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl group cursor-pointer"
                  style={{
                    background: 'white',
                    border: '2px solid rgba(255, 222, 33, 0.2)'
                  }}
                >
                  {/* Icon Circle */}
                  <div
                    className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6"
                    style={{ background: '#ffde21' }}
                  >
                    <Icon size={32} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
                  </div>

                  {/* Feature Title */}
                  <h3
                    className="text-xl font-bold mb-3"
                    style={{ color: '#2d2d2b' }}
                  >
                    {t(`smart_home_features.${feature.key}`)}
                  </h3>

                  {/* Decorative Element */}
                  <div
                    className="absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl transition-all duration-300 group-hover:h-2"
                    style={{ background: '#ffde21' }}
                  ></div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section - Modern Grid Layout */}
      <section
        className="py-16 md:py-24 relative overflow-hidden"
        style={{ background: '#2d2d2b' }}
      >
        {/* Decorative Background */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(45deg, #ffde21 25%, transparent 25%, transparent 75%, #ffde21 75%, #ffde21),
                             linear-gradient(45deg, #ffde21 25%, transparent 25%, transparent 75%, #ffde21 75%, #ffde21)`,
            backgroundSize: '60px 60px',
            backgroundPosition: '0 0, 30px 30px'
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-black mb-4"
              style={{ color: '#ffde21' }}
            >
              {dir === "rtl" ? "لماذا تختار النظام الذكي؟" : "Why Choose Smart System?"}
            </h2>
            <p
              className="text-lg max-w-2xl mx-auto"
              style={{ color: 'white', opacity: 0.8 }}
            >
              {dir === "rtl"
                ? "اكتشف المزايا التي تجعل منزلك أكثر ذكاءً وكفاءة"
                : "Discover the benefits that make your home smarter and more efficient"}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {benefitsArray.map((benefit, index) => {
              const Icon = benefitIcons[index % benefitIcons.length];
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="relative p-6 md:p-8 rounded-2xl transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl group"
                  style={{
                    background: 'rgba(250, 252, 240, 0.05)',
                    border: '2px solid rgba(255, 222, 33, 0.3)'
                  }}
                >
                  {/* Icon */}
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110"
                    style={{ background: '#ffde21' }}
                  >
                    <Icon size={24} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
                  </div>

                  {/* Benefit Text */}
                  <p
                    className="text-base md:text-lg font-medium leading-relaxed"
                    style={{ color: 'white' }}
                  >
                    {benefit}
                  </p>

                  {/* Corner Accent */}
                  <div
                    className="absolute top-4 right-4 w-2 h-2 rounded-full"
                    style={{ background: '#ffde21', opacity: 0.5 }}
                  ></div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2
              className="text-3xl md:text-4xl font-black mb-6"
              style={{ color: '#2d2d2b' }}
            >
              {dir === "rtl" ? "جاهز لتحويل منزلك؟" : "Ready to Transform Your Home?"}
            </h2>
            <p
              className="text-lg md:text-xl mb-8"
              style={{ color: '#2d2d2b', opacity: 0.7 }}
            >
              {dir === "rtl"
                ? "احصل على استشارة مجانية من خبرائنا اليوم"
                : "Get a free consultation from our experts today"}
            </p>
            <Link to="/contact">
              <button
                className="px-10 py-5 rounded-xl font-bold text-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                style={{
                  background: '#ffde21',
                  color: '#2d2d2b'
                }}
              >
                {t("homepage.buttons.contactUs")}
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
